import { EventEmitter } from '@src/slide/infrastructure/module/independent/event-emitter';
import { UserInfo } from '@tencent/docs-login/dist/utils/user';

/**
 * 事件key的枚举，所有要在此事件总线监听的都需加入此枚举中
 */
export enum EventKeys {
    SLIDE_APP_READY = 'onSlideAppReady', // slideApp初始化完成（数据完成、权限等模块初始化完成）
    FIRST_PAGE_DISPLAY = 'firstPageDisplay', // 首页渲染完成
    VISIBLE_PAGE_DISPLAY = 'visiblePageDisplay', // 首次屏幕内可见缩略图渲染完成
    MASTER_LAYOUT_CONVERTED = 'masterLayoutConverted', // master/layout转换完成
    SHOW_EDIT_AUTH_GUIDE_MOBILE = 'showEditAuthGuideMobile',
    HIDE_EDIT_AUTH_GUIDE_MOBILE = 'hideEditAuthGuideMobile',
    CLICK_AUTH_GUIDE_MOBILE = 'clickAuthGuideMobile',
    SHOW_LOGIN_GUIDE_MOBILE = 'showLoginGuideMobile',
    SHOW_LOGIN_GUIDE_PC = 'showLoginGuidePc',
    SHOW_AUTH_GUIDE_PC = 'showAuthGuidePc',
    UPLOAD_TO_CLOUD_SUCCESS = 'uploadToCloudSuccess',
    TDOC_SHOW_MENU_MORE = 'tdocShowMenuMore',
    TDOC_HIDE_MENU_MORE = 'tdocHideMenuMore',
    HIDE_BUY_BUTTON_MOBILE = 'hideBuyButtonMobile',
    IOS_KEY_BOARD_SHOW = 'iosKeyBoardShow',
    IOS_KEY_BOARD_HIDE = 'iosKeyBoardHide',
    UI_MODEL_BIND_SDK = 'UIModelBindSDK',
    ATTACH_EDITOR = 'attachEditor',
    PSKEY_ERROR = 'pskeyError',
    ALL_THUMBNAIL_SITE_COMPLETE = 'allThumbnailSiteComplete',
    ALL_SLIDE_DATA_CONVERT = 'allSlideDataConvert',
    NOTE_STATE_CHANGE = 'noteStateChange',
    WORKBENCH_ATTACH_EDITOR_DONE = 'workbenchAttachEditorDone',
    TRIGGER_COPY_EVENT = 'triggerCopyEvent',
    LINK_CLICKED = 'linkClicked',
    LINK_CLICKED_IN_FRAME = 'linkClickedInFrame',
    MOBILE_PLAYER_CENTER_TOUCHED = 'mobilePlayerCenterTouched',
    EDIT_TYPE_CHANGE = 'editTypeChange',
    SLIDE_LIST_CELL_MOUNTED = 'slideListCellMounted',
    WORKBENCH_IS_MOUNT = 'workbenchIsMount', // pages下dom节点挂载到workbench节点下
    START_PLAY = 'startPLay',
    END_PLAY = 'endPlay',
    SLIDE_PLAYER_END_PLAY = 'slidePlayerEndPlay',
    HELP_CENTER_READY_DISPLAY = 'helpCenterReadyDisplay',
    CONTROL_EVENT = 'controlEvent',
    SLIDE_DISAPPEAR_IN_LIST = 'SlideDisappearInList',
    VIDEO_FULLSCREEN_CHANGE = 'videoFullScreenChange',
    UPDATE_TITLE_SHOW_STATE = 'updateTitleShowState',
    LOGIN_USER_INFO_READY_EVENT = 'LoginUserInfoReadyEvent',
    PRESENTATION_CONVERT_START = 'presentationConvertStart',
    PRESENTATION_CONVERTED = 'presentationConverted',
    CERTIFICATION_CHANGED = 'certificationChanged',  // 认证信息变更
    START_EXPORT = 'startExport',
    /** 分片导入文档，文档数据落盘成功 */
    DOC_CREATED_SUCCESS = 'docCreatedSuccess',
    /** 批注 */
    NOTICE_COMMENT_CHANGE = 'noticeCommentChange',   // 后台通知，批注变更，包含增加、更新、删除批注
    ACTIVE_COMMENT_CHANGE = 'activeCommentChange',  // 当前选中的批注变更
    SLIDE_MODEL_CONVERTED_COMMENT = 'slideModelConvertedComment',   // slideModel sdk-ui 转换更新批注
    COMMENT_GROUP_INFO_CHANGED = 'commentGroupInfoChanged',   // 批注组内容变更
    SLIDE_CHANGE_INDEX_BEFORE = 'slideChangeIndexBefore', // 切页前
    SLIDE_CHANGE_INDEX_AFTER = 'slideChangeIndexAfter', // 切页后
    WORD_ART_RENDER = 'wordArtRender',
    MOBILE_RENAME_DIALOG_SHOW = 'mobileRenameDialogShow', // 是否显示重命名弹窗
    TRIGGER_MOBILE_MENU = 'triggerMobileMenu', // 移动端菜单展示
    RETRY_AI_WRITING = 'retryAiWriting', // 重试AI写作
    LOCAL_PASSWORD_READONLY = 'localPasswordReadonly', // 编辑密码弹框只读
}

interface EventsMap {
    [EventKeys.SLIDE_APP_READY]: { clientVars: { [key: string]: any } };
    [EventKeys.FIRST_PAGE_DISPLAY]: void;
    [EventKeys.VISIBLE_PAGE_DISPLAY]: void;
    [EventKeys.MASTER_LAYOUT_CONVERTED]: void;
    [EventKeys.SHOW_EDIT_AUTH_GUIDE_MOBILE]: void;
    [EventKeys.HIDE_EDIT_AUTH_GUIDE_MOBILE]: void;
    [EventKeys.CLICK_AUTH_GUIDE_MOBILE]: void;
    [EventKeys.SHOW_LOGIN_GUIDE_MOBILE]: void;
    [EventKeys.SHOW_LOGIN_GUIDE_PC]: void;
    [EventKeys.SHOW_AUTH_GUIDE_PC]: void;
    [EventKeys.UPLOAD_TO_CLOUD_SUCCESS]: void;
    [EventKeys.TDOC_SHOW_MENU_MORE]: void;
    [EventKeys.TDOC_HIDE_MENU_MORE]: void;
    [EventKeys.HIDE_BUY_BUTTON_MOBILE]: void;
    [EventKeys.IOS_KEY_BOARD_SHOW]: number;
    [EventKeys.IOS_KEY_BOARD_HIDE]: number;
    [EventKeys.UI_MODEL_BIND_SDK]: { slideCount: number, slides: { [key: string]: any }[] };
    [EventKeys.ATTACH_EDITOR]: void;
    [EventKeys.PSKEY_ERROR]: void;
    [EventKeys.ALL_THUMBNAIL_SITE_COMPLETE]: void;
    [EventKeys.ALL_SLIDE_DATA_CONVERT]: void;
    [EventKeys.NOTE_STATE_CHANGE]: void;
    [EventKeys.WORKBENCH_ATTACH_EDITOR_DONE]: void;
    [EventKeys.TRIGGER_COPY_EVENT]: (fn: (copyValue?: string | undefined) => boolean) => void;
    [EventKeys.LINK_CLICKED]: void;
    [EventKeys.LINK_CLICKED_IN_FRAME]: string;
    [EventKeys.MOBILE_PLAYER_CENTER_TOUCHED]: void;
    [EventKeys.EDIT_TYPE_CHANGE]: string;
    [EventKeys.SLIDE_LIST_CELL_MOUNTED]: { [key: string]: any };
    [EventKeys.WORKBENCH_IS_MOUNT]: string;
    [EventKeys.START_PLAY]: string;
    [EventKeys.END_PLAY]: string;
    [EventKeys.SLIDE_PLAYER_END_PLAY]: string;
    [EventKeys.HELP_CENTER_READY_DISPLAY]: void;
    [EventKeys.CONTROL_EVENT]: { name: string, el: string };
    [EventKeys.SLIDE_DISAPPEAR_IN_LIST]: string | null;
    [EventKeys.VIDEO_FULLSCREEN_CHANGE]: boolean;
    [EventKeys.UPDATE_TITLE_SHOW_STATE]: {
        visible: boolean,
        containerOffsetX: number,
        containerOffsetY: number,
        title: string,
    };
    [EventKeys.LOGIN_USER_INFO_READY_EVENT]: UserInfo | undefined;
    [EventKeys.PRESENTATION_CONVERT_START]: void;
    [EventKeys.PRESENTATION_CONVERTED]: void;
    [EventKeys.CERTIFICATION_CHANGED]: void;
    [EventKeys.START_EXPORT]: void;
    [EventKeys.DOC_CREATED_SUCCESS]: void;
    [EventKeys.NOTICE_COMMENT_CHANGE]: any;
    [EventKeys.ACTIVE_COMMENT_CHANGE]: { isJumpToPrevComment?: boolean };
    [EventKeys.SLIDE_MODEL_CONVERTED_COMMENT]: { slideModel: any },
    [EventKeys.COMMENT_GROUP_INFO_CHANGED]: void,
    [EventKeys.SLIDE_CHANGE_INDEX_BEFORE]: number;
    [EventKeys.SLIDE_CHANGE_INDEX_AFTER]: number;
    [EventKeys.WORD_ART_RENDER]: {
        key: string;
        src: string,
        contentArea: unknown;
        charStructListLite: unknown;
    };
    [EventKeys.MOBILE_RENAME_DIALOG_SHOW]: {
        show?: boolean
    }
    [EventKeys.TRIGGER_MOBILE_MENU]: {
        show?: boolean
    }
    [EventKeys.RETRY_AI_WRITING]: void
    [EventKeys.LOCAL_PASSWORD_READONLY]: void;
}

export default new EventEmitter<EventsMap>();
